import { lazy, Suspense, useState } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Index from "./pages/Index";
import ScrollToTop from "./components/ScrollToTop";
import ReadingProgress from "./components/ReadingProgress";
import { QuoteFormProvider } from "./components/QuoteFormContext";
import PremiumLoader from "./components/PremiumLoader";
import VisitPopup from "./components/VisitPopup";

/**
 * The home page is bundled with the entry chunk because it is the landing
 * route and its hero drives Largest Contentful Paint. Every other route is
 * split into its own chunk, so a visitor only downloads the page they asked
 * for instead of all ten.
 */
const Services = lazy(() => import("./pages/Services"));
const ServiceDetail = lazy(() => import("./pages/ServiceDetail"));
const Gallery = lazy(() => import("./pages/Gallery"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const About = lazy(() => import("./pages/About"));
const Locations = lazy(() => import("./pages/Locations"));
const LocationDetail = lazy(() => import("./pages/LocationDetail"));
const NotFound = lazy(() => import("./pages/NotFound"));

/**
 * Holds the page background at full viewport height while a route chunk
 * arrives, so a route change never collapses the layout or shifts content.
 */
const RouteFallback = () => <div className="min-h-screen bg-background" aria-hidden="true" />;

const App = () => {
  const [loaderDone, setLoaderDone] = useState(false);

  return (
    <QuoteFormProvider>
      <PremiumLoader onDone={() => setLoaderDone(true)} />
      <VisitPopup ready={loaderDone} />
      <BrowserRouter>
        <ReadingProgress />
        <ScrollToTop />
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/about" element={<About />} />
            <Route path="/services" element={<Services />} />
            <Route path="/services/:slug" element={<ServiceDetail />} />
            <Route path="/locations" element={<Locations />} />
            <Route path="/locations/:slug" element={<LocationDetail />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </QuoteFormProvider>
  );
};

export default App;
